import React, { useState } from "react"
import {
  Box, Flex, IconButton, Stack, Text,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "react-query"
import Button from "components/Buttons/Button"
import Modal from "components/dialogs/Modal"
import Confirm from "components/dialogs/Confirm/Confirm"
import CopyableLink from "components/elements/CopyableLink"
import Icon from "components/elements/Icon"
import AlertBox, { AlertBoxType } from "components/elements/AlertBox/AlertBox"
import SettingsSection from "components/settings/SettingsSection"
import Spinner from "components/elements/Spinner"
import { postApiKey, deleteApiKey } from "api/apiKeys"
import { apiKeysCacheKey, useApiKeys } from "queries/apiKeys"
import { ApiKey } from "sharedTypes"
import { asDate } from "utilities/strings"
import { successToast } from "utilities/toasts"
import { ZAPIER_APP_LINK } from "utilities/externalLinks"
import RoundedList from "../RoundedList"
import CreateApiKeyModal from "./CreateApiKeyModal"

type Props = {
  eventId: string
  enabled: boolean
}

const DateTime = ({ value }: { value: string }) => (
  <>
    <Text>{asDate(value, "MMM d, yyyy")}</Text>
    <Text>{asDate(value, "h:mm a")}</Text>
  </>
)

const Zapier = ({ eventId, enabled }: Props) => {
  const queryClient = useQueryClient()
  const { data: apiKeys = [], isLoading } = useApiKeys(eventId)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newKey, setNewKey] = useState<ApiKey | null>(null)

  const refresh = () => queryClient.invalidateQueries(apiKeysCacheKey(eventId))

  const createApiKey = (label: string) => postApiKey(eventId, label).then(({ data }) => {
    setShowCreateForm(false)
    setNewKey(data)
    refresh()
  })

  const revokeMutation = useMutation(
    (apiKey: ApiKey) => deleteApiKey(eventId, apiKey.id),
    {
      onSuccess: (_, apiKey) => {
        successToast({ title: `Revoked ${apiKey.label || "API key"}` })
        refresh()
      },
    },
  )

  return (
    <SettingsSection
      title="Zapier"
      description="Zapier lets you automate Diobox workflows with thousands of popular apps, without writing code. Send guest data to other tools, keep records in sync, or start workflows based on guest activity."
      actionButtons={[
        // Zapier's public page about Diobox: open to every plan, so people can see what it does
        <Button variant="outline" as="a" href={ZAPIER_APP_LINK} target="_blank" rel="noopener noreferrer">
          View Diobox on Zapier
        </Button>,
        <Button leftIcon="plus" isDisabled={!enabled} onClick={() => setShowCreateForm(true)}>
          New API Key
        </Button>,
      ]}
    >
      <RoundedList>
        {isLoading && <Box h={20}><Spinner heightFull size="md" /></Box>}
        {!isLoading && apiKeys.length === 0 && (
          <Flex px={6} py={5} justify="center">
            <Text color="gray.600">No API keys have been created for this event.</Text>
          </Flex>
        )}
        {apiKeys.length > 0 && (
          <Flex px={6} py={3} align="center" gap={4}>
            <Text fontWeight="semibold" flex={2}>NAME</Text>
            <Text fontWeight="semibold" flex={2}>CREATED ON</Text>
            <Text fontWeight="semibold" flex={3}>API KEY</Text>
            <Text fontWeight="semibold" flex={2}>LAST USED</Text>
            <Box w={8} />
          </Flex>
        )}
        {apiKeys.map((apiKey) => (
          <Flex px={6} py={3} align="center" gap={4} key={apiKey.id}>
            <Text flex={2}>{apiKey.label || "Untitled"}</Text>
            <Box flex={2}><DateTime value={apiKey.createdAt} /></Box>
            <Text flex={3} fontFamily="mono" color="gray.600">{`••••••••••••${apiKey.tokenLastFour}`}</Text>
            <Box flex={2}>
              {apiKey.lastUsedAt ? <DateTime value={apiKey.lastUsedAt} /> : <Text color="gray.600">Never</Text>}
            </Box>
            <Confirm
              confirmationTitle="Revoke API Key"
              confirmationText={`Are you sure you want to revoke "${apiKey.label || "this key"}"? Any Zaps using it will stop working.`}
              confirmButtonLabel="Revoke"
            >
              <IconButton
                aria-label="Revoke API key"
                variant="ghost"
                size="sm"
                color="red.500"
                icon={<Icon icon="cancel-circle" size={5} />}
                // mutateAsync: Confirm only waits for a promise before closing.
                onClick={() => revokeMutation.mutateAsync(apiKey)}
              />
            </Confirm>
          </Flex>
        ))}
      </RoundedList>

      {showCreateForm && (
        <CreateApiKeyModal onClose={() => setShowCreateForm(false)} onSubmit={createApiKey} />
      )}

      {newKey && (
        <Modal show size="md" hide={() => setNewKey(null)} title="API Key Created">
          <Stack spacing={4}>
            <Text>Your API key is ready. Copy it and paste it into Zapier when prompted.</Text>
            <CopyableLink label="API Key" value={newKey.plaintextToken || ""} />
            <AlertBox
              type={AlertBoxType.Warning}
              text="Copy and save this API key now. For security, it won't be shown again after you close this window."
            />
          </Stack>
        </Modal>
      )}
    </SettingsSection>
  )
}

export default Zapier
